import { useCallback } from 'react';
import { useAgentStore } from '@/stores/agentStore';
import { stripThinkingAndToolCallTags } from '../helpers';
// 🔵 使用统一的 ProcessLogs 管理器（仅使用 extract 助手，不再做 COLLAB_STATUS 解析）
import { addProcessLogsFromTextChunk } from '../processLogManager';

// 🔴 流式 Markdown 缓冲区
// 这些不完整结构被暂存到 pendingMarkdown，等待下一个 chunk 拼接后完整处理
// 🔴 P1-4: 增强链接正则以支持嵌套括号 URL 和中文字符标签
// 🔴 P0-2: 调换模式 1/2 顺序，确保 [label]( URL 片段优先被识别为不完整链接
// 🔴 P0-1 修复: 调换模式 1 和模式 2 的检查顺序
// 模式 1（不完整的链接 [label](... 缺少 )) 必须优先于模式 2（不完整的标签 [label 缺少 ]）
// 原因: 当 fullChunk = "[京东](https://..." 时，模式 2 的 [^[\]]* 会读到 ] 才停止，导致无法匹配
// 但如果模式 1 先检查，会正确识别 "[京东](https://..." 为不完整链接并缓冲
const INCOMPLETE_MARKDOWN_PATTERNS: Array<{ regex: RegExp; groupIndex: number }> = [
  // 模式 1: [text](url → 缺少闭合 )，支持 URL 中的嵌套括号（如 wiki/Article_(1)）
  // 必须放在最前面：确保像 [京东](https://... 这种部分链接优先被识别为完整链接的一部分
  { regex: /\[([^\]]*?)\]\((?:[^()]|\([^()]*\))*$/, groupIndex: 1 },
  // 模式 2: [text → 缺少 ]（链接标签本身被 chunk 分割）
  // 排除纯数字 [1][2] 等（非链接格式，不应被缓冲为不完整链接）
  { regex: /\[([^[\]\d][^[\]]{0,200})$/, groupIndex: 1 },
  // 🔴 P0-2: 模式 3 — 完整标签 [label] 位于 chunk 末尾
  // 最常见的 SSE 切分点：AI 输出 "[京东]" 后被切分，下一个 chunk 以 "(URL)" 开头
  // 排除: 纯数字 [1][2]（非链接格式）、全大写状态标签 [INFO][ERROR]
  { regex: /\[([^[\]\d][^[\]]{0,30})\](?=\s*$)/, groupIndex: 1 },
  // **text → 缺少结尾 **
  { regex: /\*\*([^*]{0,200})$/, groupIndex: 1 },
  // `code → 缺少结尾 `
  { regex: /`([^`]{0,200})$/, groupIndex: 1 },
  // 🔴 M2-修复: 不完整表格行（流式输出时表格行被 chunk 分割）
  // 场景：chunk 最后一行是 "| 型号 | 价格 | 来"（以 | 开头，含多个 |，但不以 | 结尾）
  // 判定: 文本最后一行以 | 开头 + 至少包含 2 个后续 |（即至少 3 个单元格）+ 不以 | 结尾
  // 收紧条件：要求至少 2 个后续 |，降低含管道符的普通文本被误判的概率
  { regex: /(?:^|\n)\|(?:[^|\n]*\|){2,}[^|\n]*(?!\|)\s*$/, groupIndex: 0 },
];

/**
 * 从文本中提取可能被分割的 markdown 尾部
 * @param text 输入文本
 * @returns { processed: 去除尾部后的文本, pending: 被提取的尾部（不含前导内容） }
 */
function extractPendingMarkdown(text: string): { processed: string; pending: string } {
  for (const { regex } of INCOMPLETE_MARKDOWN_PATTERNS) {
    const match = text.match(regex);
    if (match) {
      const pending = match[0];
      const processed = text.slice(0, -pending.length);
      // 只有当 processed 非空（至少有前导内容）或 pending 确实是不完整模式时才暂存
      // P4: 增加对表格行 | 的检测（表格行可能以 | 开头且无其他前缀内容）
      if (processed.length > 0 || pending.includes('[') || pending.includes('*') || pending.includes('`') || pending.includes('|')) {
        return { processed, pending };
      }
    }
  }
  return { processed: text, pending: '' };
}

/**
 * 检查文本是否以不完整的 markdown 结构结尾
 */
function hasIncompleteMarkdownSuffix(text: string): boolean {
  for (const { regex } of INCOMPLETE_MARKDOWN_PATTERNS) {
    if (regex.test(text)) {
      return true;
    }
  }
  return false;
}

export function useStreamChunkHandler() {
  const handleChunk = useCallback((
    chunk: string,
    state: {
      accumulatedText: string;
      accumulatedThinking: string;
      lastParsedLength: number;
      streamSessionId: string;
      // ⚠️ P1-5: 标志位已移除，改用 store.activeReplyBuilder.type 判断场景
    },
  ) => {
    // 🔴 F-13: 先记录 chunk 到达时间（搜索/确认场景也要记录，供 polling 判断 SSE 是否仍活跃）
    // P0-2 原实现放在 builder 判断之后，导致搜索场景 bumpChunkReceivedAt 不被调用
    useAgentStore.getState().bumpChunkReceivedAt?.();

    // 🔵 P1-5: 用 builder.type 判断，而非独立标志位
    // 当 builder.type === 'search' 或 'generation_confirm' 时，抑制普通文本流，
    // 避免搜索卡片 / 确认面板 与流式文本同时显示
    // 但允许错误/警告类文本通过，避免搜索部分失败时用户看不到任何提示
    const builder = useAgentStore.getState().activeReplyBuilder;

    // 🔴 P0(v11): 提前检测搜索意图标签，将 builder.type 设为 'search'
    // 避免主 LLM 输出的文本在 search_result 事件到达前以纯文本形式渲染
    // 当后续 search_result 事件到达时，phase 不会从 'text' 切换到 'search'，消除闪烁
    if (builder && !builder.type) {
      const searchIntentDetected =
        chunk.includes('[CALL_AGENT:search-analyst]') ||
        chunk.includes('[CALL_AGENT_ASYNC:search-analyst]') ||
        chunk.includes('[CALL_AGENT:search_skill]') ||
        chunk.includes('[CALL_AGENT_ASYNC:search_skill]');
      if (searchIntentDetected) {
        useAgentStore.getState().updateActiveReplyBuilder({
          type: 'search',
          text: '',
          pendingMarkdown: undefined,
        });
        // 同步清除 streamingText，避免残留文本显示
        useAgentStore.getState().setStreamingText(null);
        // 🔴 P0(v15): 重置 accumulatedText，防止主 LLM 在 CALL_AGENT 标签之前
        // 输出的文本（如"我来帮你搜索"）泄漏到最终消息中
        // 场景：LLM 先输出"我来帮你搜索" → 再输出 [CALL_AGENT:search-analyst]
        // 如果不重置，accumulatedText 仍保留旧文本，流结束时可能被写入最终消息
        return { ...state, accumulatedText: '', lastParsedLength: 0 };
      }
    }

    if (builder && (builder.type === 'search' || builder.type === 'generation_confirm')) {
      // 🔴 P2-方案4: 搜索/确认场景下过滤后端推送的错误/警告文本
      // 后端 _handle_error 在搜索数据已推送后仍可能推送错误文本到 chunk_queue
      // 这些文本不应追加到 streamingText，否则会与搜索卡片同时显示
      const isErrorMessage = /搜索执行失败|搜索请求被拒绝|搜索已获取部分结果|部分内容获取失败|请稍后重试/i.test(chunk);
      if (isErrorMessage && builder.type === 'search') {
        // 将错误/警告文本附加到 builder 的 warning 字段，供搜索卡片内部展示
        useAgentStore.getState().updateActiveReplyBuilder({
          warning: ((builder as any).warning || '') + chunk,
        });
      } else if (!isErrorMessage) {
        // 🔴 P0(v4): 使用 markdown 结构缓冲区处理被 SSE chunk 分割的 markdown
        // 问题：LLM 输出的 [网站名称](URL) 链接可能被切割，如 "[淘宝](https://" 和 "taobao.com)"
        // 解决方案：暂存可能被分割的尾部到 pendingMarkdown，新 chunk 到达时先拼接再处理
        // 🔴 P0修复(v3): 使用 stripThinkingAndToolCallTags(chunk, true) 保留空白符
        // v2 版本完全移除标签清理导致流式中出现 [COLLAB_STATUS:...] 等原始标签文本
        // v3 版本：preserveWhitespace=true 保留首尾换行（.trim() 不调用），
        //          同时清除完整标签和不完整标签碎片，避免标签文本外泄
        const cleanedChunk = stripThinkingAndToolCallTags(chunk, true);
        if (cleanedChunk) {
          // 获取当前的 markdown 缓冲区
          const currentPending = builder.pendingMarkdown || '';
          // 将缓冲区的内容拼接到新 chunk 前面，形成完整的 markdown 结构
          const fullChunk = currentPending + cleanedChunk;
          // 从完整 chunk 中提取可能被分割的尾部
          const { processed, pending } = extractPendingMarkdown(fullChunk);
          // 处理换行逻辑
          const currentText = builder.text || '';
          const needsNewline = processed.startsWith('|')
            && currentText.length > 0
            && !currentText.endsWith('\n')
            && !currentText.endsWith('|');
          // 只有当有实际内容要追加时才更新 builder
          const newText = currentText + (needsNewline ? '\n' : '') + processed;
          if (newText !== currentText || pending !== currentPending) {
            useAgentStore.getState().updateActiveReplyBuilder({
              text: newText || undefined,
              pendingMarkdown: pending || undefined,  // 更新缓冲区（空字符串转为 undefined）
            });
          }
        }
      }
      return state;
    }

    // 过滤搜索数据前缀（应由 SSE 事件处理，不应作为普通文本显示）
    if (chunk.startsWith('__SEARCH_DATA__:')) return state;

    // 收到替换信号：清空已有文本，后续内容将替换而非追加
    if (chunk === '__REPLACE__') {
      const store = useAgentStore.getState();
      const currentActiveSessionId = store.sessionId;
      if (currentActiveSessionId === state.streamSessionId) {
        store.setStreamingText('');
        // 🔵 v3-#5: 同步清空 builder.text，避免 __REPLACE__ 和下一个 chunk 之间 builder 保留旧内容
        if (store.activeReplyBuilder) {
          store.updateActiveReplyBuilder({ text: '' });
        }
      }
      return { accumulatedText: '', lastParsedLength: 0 };
    }

    const accumulatedText = state.accumulatedText + chunk;

    if (!state.accumulatedThinking && accumulatedText.length > 0) {
      // 仅当 chunk 包含实际内容文本（而非纯状态标签）时才清除 thinking 计时器
      // [COLLAB_STATUS:...] 等标签是进度通知，不是实际回复内容
      const strippedOfStatusTags = chunk.replace(/\[COLLAB_STATUS:[^\]]*\][\s\S]*?\[\/COLLAB_STATUS\]/g, '');
      const cleanedChunk = strippedOfStatusTags.replace(/\[[A-Z_]+(?::[^\]]*)?\]/g, '').replace(/<[^>]+>/g, '').trim();
      if (cleanedChunk.length > 0) {
        useAgentStore.getState().clearThinkingTimer();
        useAgentStore.getState().setIsThinking(false);
      }
    }

    // 🔵 P2-3: chunk 中的 COLLAB_STATUS 只剥离不解构
    // SSE 的 collab_status 事件（onCollabStatus 回调）已经是协作日志的唯一来源
    // chunk 中出现的同名标签视为"残留伪标签"，仅 strip 不 addProcessLogFromCollabStatus
    // 避免：1) O(n²) 累积匹配  2) 与 callbacks 重复解析 → 日志去重命中反而吞掉后续真正的更新

    // 🔵 使用统一管理器：从累积文本中提取动态日志（自动去重）
    let lastParsedLength = state.lastParsedLength;
    if (accumulatedText.length - lastParsedLength > 200) {
      addProcessLogsFromTextChunk(accumulatedText);
      lastParsedLength = accumulatedText.length;
    }

    const currentActiveSessionId = useAgentStore.getState().sessionId;
    if (currentActiveSessionId === state.streamSessionId) {
      const displayText = stripThinkingAndToolCallTags(accumulatedText);
      useAgentStore.getState().setStreamingText(displayText);
    }

    return { accumulatedText, lastParsedLength };
  }, []);

  return { handleChunk };
}
